import React, { useCallback, useRef } from "react";

const HEADER_BOTTOM_GAP_PX = 12;

// The panel is absolutely positioned over the search table, so the search
// table's padding must grow with it. Both read this CSS variable, set
// imperatively during drag to avoid re-rendering the search table.
export function useMatchesPanelExtraHeightVariable() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const setMatchesPanelExtraHeight = useCallback((px: number) => {
        sectionRef.current?.style.setProperty(
            "--matches-panel-extra-height",
            `${px}px`,
        );
    }, []);
    return { sectionRef, setMatchesPanelExtraHeight };
}

// Growth is bounded by the panel's previous sibling (the search table
// container); without it the panel stops growing rather than covering the
// search header.
export function useMatchesPanelResize(
    onExtraHeightChange: (px: number) => void,
) {
    const matchesPanelRef = useRef<HTMLDivElement>(null);
    const extraHeightRef = useRef(0);
    const dragStateRef = useRef<{
        startClientY: number;
        startExtraHeight: number;
        maxExtraHeight: number;
    } | null>(null);

    const computeMaxExtraHeight = useCallback((): number => {
        const panel = matchesPanelRef.current;
        if (!panel) {
            return extraHeightRef.current;
        }
        const tableContainer = panel.previousElementSibling;
        if (!(tableContainer instanceof HTMLElement)) {
            return extraHeightRef.current;
        }
        const panelTop = panel.getBoundingClientRect().top;
        const boundaryTop = tableContainer.getBoundingClientRect().top;
        const availableUpward = Math.max(
            0,
            panelTop - boundaryTop - HEADER_BOTTOM_GAP_PX,
        );
        return extraHeightRef.current + availableUpward;
    }, []);

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            dragStateRef.current = {
                startClientY: event.clientY,
                startExtraHeight: extraHeightRef.current,
                maxExtraHeight: computeMaxExtraHeight(),
            };
        },
        [computeMaxExtraHeight],
    );

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const state = dragStateRef.current;
            if (!state) {
                return;
            }
            const deltaY = state.startClientY - event.clientY;
            const next = Math.min(
                state.maxExtraHeight,
                Math.max(0, state.startExtraHeight + deltaY),
            );
            extraHeightRef.current = next;
            onExtraHeightChange(next);
        },
        [onExtraHeightChange],
    );

    const onPointerUp = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!dragStateRef.current) {
                return;
            }
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
            dragStateRef.current = null;
        },
        [],
    );

    return {
        matchesPanelRef,
        resizeHandleProps: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
        },
    };
}
